import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Add, DeleteOutline, MoreTime } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Dispatch, MouseEvent, SetStateAction, useState } from "react";
import { addDays } from "date-fns";

import InternalUserAutocomplete from "../../../shared/InternalUserAutocomplete";
import { InternalUser } from "../../../types/app";
import { FullContact } from "../../types";
import {
  ContactInteraction,
  ContactInteractionFollowUp,
  InteractionEvent,
  InteractionReason,
  InteractionSource,
  InteractionType
} from "./types";

interface EventOption extends InteractionEvent {
  isNew?: boolean;
}

const filterEvents = createFilterOptions<EventOption>();
const normalizeEventName = (name: string) => name.trim().toLowerCase();

interface IProps {
  open: boolean;
  onClose: () => void;
  interaction: ContactInteraction;
  setInteraction: Dispatch<SetStateAction<ContactInteraction | undefined>>;
  interactionTypeOptions: InteractionType[];
  interactionReasonOptions: InteractionReason[];
  interactionEventOptions: InteractionEvent[];
  interactionSourceOptions: InteractionSource[];
  onSubmit: (interaction: ContactInteraction, colleaguesIds?: number[], eventName?: string) => Promise<void>;
  colleagues: FullContact[];
}

const AddOrEditInteractionModal = ({
  open,
  onClose,
  onSubmit,
  interaction,
  setInteraction,
  interactionTypeOptions,
  interactionReasonOptions,
  interactionEventOptions,
  interactionSourceOptions,
  colleagues
}: IProps) => {
  const toStoredDateString = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12).toISOString();
  };

  const isEventSelected = interaction.typeId != null &&
    interaction.typeId === interactionTypeOptions.find((option) => option.name === "Event")?.id;
  const isAddNewInteraction = !Boolean(interaction.id);
  const selectedReason = interactionReasonOptions.find((option) => option.id === interaction.interactionReasonId);
  const showOrderNumber = selectedReason?.name === "Title Order Received" || selectedReason?.name === "Title Order Closed";

  const [selectedColleagues, setSelectedColleagues] = useState<FullContact[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventOption | null>(
    interactionEventOptions.find((option) => option.id === interaction.eventId) || interaction.event || null
  );
  const [eventName, setEventName] = useState(selectedEvent?.name || "");
  const isEventSelectionPending = isEventSelected && Boolean(eventName.trim()) &&
    (!selectedEvent || normalizeEventName(eventName) !== normalizeEventName(selectedEvent.name));
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [presetAnchorEl, setPresetAnchorEl] = useState<HTMLElement | null>(null);
  const [presetFollowUpIndex, setPresetFollowUpIndex] = useState<number | null>(null);

  const handleInteractionDateChange = (newDate: Date | null) => {
    if (newDate) {
      setInteraction({ ...interaction, interactionDate: toStoredDateString(newDate) });
    }
  };

  const handleSubmit = async () => {
    if (isSaving || isEventSelectionPending) return;
    setIsSaving(true);
    setSaveError(null);
    const colleaguesToSubmit = selectedColleagues?.map((c) => c.id).filter(Boolean) || [];
    try {
      await onSubmit(
        {
          ...interaction,
          eventId: isEventSelected && selectedEvent && !selectedEvent.isNew ? selectedEvent.id : null,
          followUps: sortFollowUps((interaction.followUps || []).filter(hasValidFollowUpDate))
        },
        colleaguesToSubmit,
        isEventSelected && selectedEvent?.isNew ? selectedEvent.name : undefined
      );
      onClose();
    } catch {
      setSaveError("Unable to save the interaction. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = (event: object, reason: string) => {
    if (isSaving || reason == "escapeKeyDown" || reason == "backdropClick") return;
    onClose();
  };

  const sortFollowUps = (followUps: ContactInteractionFollowUp[]) => {
    return [...followUps].sort(
      (left, right) =>
        new Date(left.followUpDate || "").getTime() - new Date(right.followUpDate || "").getTime()
    );
  };

  const hasValidFollowUpDate = (
    followUp: ContactInteractionFollowUp
  ): followUp is ContactInteractionFollowUp & { followUpDate: string } => {
    return Boolean(followUp.followUpDate) && !Number.isNaN(new Date(followUp.followUpDate!).getTime());
  };

  const getEditableFollowUps = () => {
    return interaction.followUps?.length ? interaction.followUps : [{ followUpDate: null }];
  };

  const hasAnyValidFollowUp = () => {
    return (interaction.followUps || []).some(hasValidFollowUpDate);
  };

  const addEmptyFollowUp = () => {
    setInteraction({
      ...interaction,
      followUps: [...getEditableFollowUps(), { followUpDate: null }]
    });
  };

  const updateFollowUpDate = (index: number, date: Date | null) => {
    const followUps = getEditableFollowUps();
    const updatedFollowUps = followUps.map((followUp, followUpIndex) =>
      followUpIndex === index
        ? {
            ...followUp,
            followUpDate: date && !Number.isNaN(date.getTime()) ? toStoredDateString(date) : null
          }
        : followUp
    );

    setInteraction({
      ...interaction,
      followUps: updatedFollowUps
    });
  };

  const removeFollowUp = (index: number) => {
    setInteraction({
      ...interaction,
      followUps: getEditableFollowUps().filter((_, followUpIndex) => followUpIndex !== index)
    });
  };

  const openPresetMenu = (event: MouseEvent<HTMLElement>, index: number) => {
    setPresetAnchorEl(event.currentTarget);
    setPresetFollowUpIndex(index);
  };

  const closePresetMenu = () => {
    setPresetAnchorEl(null);
    setPresetFollowUpIndex(null);
  };

  const applyPresetFollowUp = (days: number) => {
    if (presetFollowUpIndex != null) {
      updateFollowUpDate(presetFollowUpIndex, addDays(new Date(), days));
    }

    closePresetMenu();
  };

  const followUpOptions = [
    { value: 30, label: "30 Days" },
    { value: 60, label: "60 Days" },
    { value: 90, label: "90 Days" },
    { value: 120, label: "120 Days" },
    { value: 180, label: "180 Days" }
  ];

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="add-interaction-modal">
      <DialogTitle>
        <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
          {interaction.id ? "Edit" : "Add"} Interaction
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box component="fieldset" disabled={isSaving} sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={isEventSelected ? 6 : 12}>
            <FormControl fullWidth sx={{ mb: 1, mt: 1 }}>
              <InputLabel id="contact-method-label">Contact Method</InputLabel>
              <Select
                labelId="contact-method-label"
                value={interaction.typeId || ""}
                label="Contact Method"
                onChange={(e) => {
                  const typeId = Number(e.target.value);
                  const isEvent = interactionTypeOptions.find((option) => option.id === typeId)?.name === "Event";
                  if (!isEvent) {
                    setSelectedEvent(null);
                    setEventName("");
                  }
                  setInteraction({
                    ...interaction,
                    typeId,
                    eventId: isEvent ? interaction.eventId : null
                  });
                }}
              >
                {interactionTypeOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {isEventSelected && (
            <Grid item xs={12} md={6}>
              <Autocomplete<EventOption>
                fullWidth
                disabled={isSaving}
                selectOnFocus
                clearOnBlur={false}
                handleHomeEndKeys
                options={selectedEvent?.isNew ? [...interactionEventOptions, selectedEvent] : interactionEventOptions}
                value={selectedEvent}
                inputValue={eventName}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  option.id === value.id && normalizeEventName(option.name) === normalizeEventName(value.name)
                }
                filterOptions={(options, params) => {
                  const name = params.inputValue.trim();
                  const filtered = filterEvents(options, { ...params, inputValue: name });
                  const exists = options.some(
                    (option) => normalizeEventName(option.name) === normalizeEventName(name)
                  );

                  if (name && !exists) {
                    filtered.push({ id: 0, name, isNew: true });
                  }

                  return filtered;
                }}
                onChange={(_, option) => {
                  setSelectedEvent(option);
                  setEventName(option?.name || "");
                  setInteraction({ ...interaction, eventId: option && !option.isNew ? option.id : null });
                }}
                onInputChange={(_, name, reason) => {
                  if (reason !== "input") return;
                  setEventName(name);
                  setSelectedEvent(null);
                  setInteraction({ ...interaction, eventId: null });
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.isNew ? "new-event" : option.id}>
                    {option.isNew ? `Add “${option.name}”` : option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Event Name" />
                )}
                sx={{ mt: 1 }}
              />
            </Grid>
          )}
        </Grid>

        <Grid item xs={12} md={12}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="contact-interaction-reason-label">Reason</InputLabel>
            <Select
              labelId="contact-interaction-reason-label"
              value={interaction.interactionReasonId || ""}
              label="Contact Reason"
              onChange={(e) => {
                const reasonId = e.target.value as number;
                const reasonName = interactionReasonOptions.find((o) => o.id === reasonId)?.name;
                const category =
                  reasonName === "Title Order Received"
                    ? "Received"
                    : reasonName === "Title Order Closed"
                    ? "Closed"
                    : null;
                setInteraction({
                  ...interaction,
                  interactionReasonId: reasonId,
                  interactionCategory: category
                });
              }}
            >
              {interactionReasonOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {showOrderNumber && (
          <Grid item xs={12} md={12}>
            <TextField
              fullWidth
              label="Order Number"
              value={interaction.orderNumber || ""}
              onChange={(e) => setInteraction({ ...interaction, orderNumber: e.target.value || null })}
              sx={{ mb: 2 }}
            />
          </Grid>
        )}
        <Grid item xs={12} md={12}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="contact-interaction-source-label">Source</InputLabel>
            <Select
              labelId="contact-interaction-source-label"
              value={interaction.interactionSourceId || ""}
              label="Source"
              onChange={(e) => {
                setInteraction({
                  ...interaction,
                  interactionSourceId: e.target.value as number
                });
              }}
            >
              {interactionSourceOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <DatePicker
              label="Date"
              value={interaction.interactionDate ? new Date(interaction.interactionDate) : null}
              onChange={handleInteractionDateChange}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </FormControl>
        </LocalizationProvider>
        <FormControl component="fieldset" sx={{ mb: 3, width: "100%" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Follow Ups</Typography>
          </Stack>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={1.5}>
              {getEditableFollowUps().map((followUp, index) => (
                <Stack key={followUp.id ?? `new-follow-up-${index}`} direction="row" spacing={1} alignItems="center">
                  <Box sx={{ flex: 1, position: "relative" }}>
                    <DatePicker
                      label={`Follow Up ${index + 1}`}
                      value={followUp.followUpDate ? new Date(followUp.followUpDate) : null}
                      onChange={(date) => updateFollowUpDate(index, date instanceof Date ? date : null)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: {
                            "& .MuiInputBase-input": {
                              pr: 4
                            }
                          }
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(event) => openPresetMenu(event, index)}
                      aria-label={`follow up ${index + 1} presets`}
                      sx={{
                        position: "absolute",
                        right: 40,
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 1
                      }}
                    >
                      <MoreTime fontSize="small" />
                    </IconButton>
                  </Box>
                  <IconButton size="small" onClick={() => removeFollowUp(index)} aria-label={`remove follow up ${index + 1}`}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </LocalizationProvider>
          {hasAnyValidFollowUp() && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button size="small" startIcon={<Add fontSize="small" />} onClick={addEmptyFollowUp}>
                Add Follow Up
              </Button>
            </Stack>
          )}
          <Menu anchorEl={presetAnchorEl} open={Boolean(presetAnchorEl)} onClose={closePresetMenu}>
            {followUpOptions.map((option) => (
              <MenuItem key={option.value} onClick={() => applyPresetFollowUp(option.value)}>
                {option.label}
              </MenuItem>
            ))}
          </Menu>
        </FormControl>
        <Autocomplete
          freeSolo
          fullWidth
          options={[
            "Left voicemail",
            "Spoke with contact",
            "Discussed order status",
            "Scheduled follow-up",
            "No answer",
            "Sent information",
            "Addressed concerns"
          ]}
          value={interaction.notes || ""}
          onChange={(_, newValue) => setInteraction({ ...interaction, notes: newValue || "" })}
          onInputChange={(_, newValue) => setInteraction({ ...interaction, notes: newValue || "" })}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Notes"
              multiline
              rows={4}
              placeholder="Select a quick note or type your own..."
            />
          )}
          sx={{ mb: 3, mt: 3 }}
        />
        <InternalUserAutocomplete
          label="Additional Attendee"
          limitToSalesReps
          sx={{ mb: 3 }}
          value={interaction.additionalAttendee || null}
          onChange={(r) => {
            const rep = r as InternalUser | null;
            setInteraction({
              ...interaction,
              additionalAttendee: rep || null
            });
          }}
        />
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InternalUserAutocomplete
            label="Cultivator"
            value={interaction.clientCultivator || null}
            onChange={(r) => {
              const rep = r as InternalUser | null;
              setInteraction({
                ...interaction,
                clientCultivator: rep || null
              });
            }}
          />
        </FormControl>
        {Boolean(colleagues?.length) && isAddNewInteraction && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Autocomplete
              onChange={(_, option) => setSelectedColleagues(option || null)}
              options={colleagues}
              multiple
              value={selectedColleagues}
              fullWidth
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "new-password"
                  }}
                  fullWidth
                  label={"Add to Colleague"}
                />
              )}
              getOptionLabel={(option) => (option?.firstName || "") + " " + (option?.lastName || "")}
            />
          </FormControl>
        )}
        </Box>
        {saveError && <Typography color="error" role="alert">{saveError}</Typography>}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={isSaving}>
          CANCEL
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving || isEventSelectionPending}>
          {isSaving ? "SAVING..." : "SAVE"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOrEditInteractionModal;
